import { memo, startTransition, useEffect, useState } from "react";
import { Layer, Rect, Stage } from "react-konva";

const excludePausedRanges = (originalRanges, pausedRanges) => {
  if (!pausedRanges || pausedRanges.length === 0) return originalRanges;
  if (!originalRanges || originalRanges.length === 0) return [];

  let result = [];

  for (const [start, end] of originalRanges) {
    let currentStart = start;

    // Sort paused ranges that overlap with current range
    const relevantPauses = pausedRanges
      .filter(([pStart, pEnd]) => !(pEnd < start || pStart > end))
      .sort(([a], [b]) => a - b);

    for (const [pauseStart, pauseEnd] of relevantPauses) {
      // Add range before pause if it exists
      if (currentStart < pauseStart) {
        result.push([currentStart, pauseStart - 1]);
      }
      currentStart = pauseEnd + 1;
    }

    // Add remaining range after last pause if it exists
    if (currentStart <= end) {
      result.push([currentStart, end]);
    }
  }

  return result;
};

const ProgressBar = ({
  videoDomWidth,
  progressTracks,
  pausedList,
  frameStart,
  frameEnd,
  percentRef,
  onProgressClick,
}) => {
  const [progressBars, setProgressBars] = useState([]);
  const [pausedBars, setPausedBars] = useState([]);
  const [totalBars, setTotalBars] = useState([]);
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const tracks = progressTracks.filter(
      ({ frame_start, frame_end }) =>
        !(frame_end <= frameStart || frame_start >= frameEnd)
    );
    if (!tracks || tracks.length === 0) return;

    tracks.sort((a, b) => a.frame_start - b.frame_start);

    let barCode = [];
    const length = frameEnd - frameStart + 1;
    const accumulatedFrameRanges = [];

    for (let i = 0; i < tracks.length; i++) {
      const { frame_start, frame_end } = tracks[i];

      // Calculate bar properties
      const x = (videoDomWidth * (frame_start - frameStart)) / length;
      const width = (videoDomWidth * (frame_end - frame_start + 1)) / length;

      // Check if we can merge the current track with the last one in barCode
      if (barCode.length > 0 && frame_start === tracks[i - 1].frame_end + 1) {
        // Merge with the last bar
        const lastBar = barCode[barCode.length - 1];
        lastBar.width += width; // Extend the width of the last bar
      } else {
        // Push new bar to barCode
        barCode.push({
          x,
          y: 0,
          width,
          height: 30,
          fill: "red",
        });
      }

      // Accumulate the total frames covered by the tracks
      if (i === 0 || frame_start > tracks[i - 1].frame_end) {
        accumulatedFrameRanges.push([
          Math.max(frame_start, frameStart),
          Math.min(frame_end, frameEnd),
        ]);
      } else {
        accumulatedFrameRanges.push([
          Math.max(tracks[i - 1].frame_end, frameStart),
          Math.min(frame_end, frameEnd),
        ]);
      }
    }

    // Get the actual active ranges after excluding paused ranges
    const activeRanges = excludePausedRanges(
      accumulatedFrameRanges,
      pausedList
    );

    // Calculate accumulated frames from active ranges
    const accumulatedFrames = activeRanges.reduce(
      (sum, [start, end]) => sum + (end - start + 1),
      0
    );

    const taskLength = frameEnd - frameStart + 1;
    const percentage = (accumulatedFrames / taskLength) * 100;
    setPercent(percentage.toFixed(2));
    startTransition(() => {
      setProgressBars(barCode);
    });
  }, [frameStart, frameEnd, videoDomWidth, progressTracks, pausedList]);

  useEffect(() => {
    if (!pausedList) return;
    let barCode = [];
    const length = frameEnd - frameStart + 1;
    for (let i = 0; i < pausedList.length; i++) {
      const [frame_start, frame_end] = pausedList[i];

      // Calculate bar properties
      const x = (videoDomWidth * (frame_start - frameStart)) / length;
      const width = (videoDomWidth * (frame_end - frame_start + 1)) / length;

      barCode.push({
        x,
        y: 0,
        width,
        height: 30,
        fill: "white",
        opacity: 0.35,
      });
    }
    startTransition(() => {
      setPausedBars(barCode);
    });
  }, [frameStart, frameEnd, videoDomWidth, pausedList]);

  useEffect(() => {
    setTotalBars([...progressBars, ...pausedBars]);
  }, [progressBars, pausedBars]);

  return (
    <div className="tw-relative" style={{ display: "flex", width: "100%" }}>
      <Stage width={videoDomWidth} height={30}>
        <Layer>
          <Rect x={0} y={0} width={videoDomWidth} height={30} fill="#28A745" />
          {totalBars &&
            totalBars.map(({ x, y, width, height, fill, opacity }, i) => (
              <Rect
                key={i}
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fill}
                opacity={opacity}
              />
            ))}
        </Layer>
      </Stage>
      <div
        ref={percentRef}
        className="super-track-markup-progress__percent"
        style={{ height: "30px" }}
        onClick={onProgressClick}
      >
        {percent}%
      </div>
    </div>
  );
};

export default memo(ProgressBar);
